import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { csrfManager, initAuthSingletons, isCsrfInvalidError } from '@ams/shared/auth';
import { AuthProvider } from './auth/AuthContext.js';
import { createAuthService, type AuthService } from './auth/authService.js';
import { LoginPage } from './routes/LoginPage.js';
import { SettingsPage } from './routes/SettingsPage.js';
import { ProtectedRoute } from './routes/ProtectedRoute.js';
import { AppShell } from './shell/AppShell.js';
import { ResolveHome } from './shell/ResolveHome.js';
import { ExpoAmsRouter } from './expoams/ExpoAmsRouter.js';
import { SddLoading } from './station-data-dashboard/SddLoading.js';

// materials-03b Phase 2: bootstrap the @ams/shared/auth singletons ONCE at
// module-eval time, BEFORE any lazy() chunk is referenced and BEFORE
// defaultAuthService is constructed (which captures the shared csrfManager).
// Module packages (BOM, inventory) and admin/expoams/dataVis/SDD services all
// read the same singleton via @ams/shared/auth.
initAuthSingletons({
  apiBase: import.meta.env.VITE_API_URL ?? '',
  functionsBase: import.meta.env.VITE_OPS_FUNCTIONS_BASE ?? '',
});

// Plan 12 Slice A: dynamic-import the admin module so non-admin members
// don't pay the inovua-grid + admin-dialogs bundle cost on first paint.
// AdminRouter and everything it transitively imports (5 tables, 7 dialogs,
// PrivilegeGrid, TypedConfirmDialog, lockout-guard, csv-export, the
// inovua-grid CSS) end up in their own chunk loaded on first /admin nav.
const AdminRouter = lazy(() =>
  import('./admin/AdminRouter.js').then((m) => ({ default: m.AdminRouter })),
);

// Plan 24 — CSV User Import page. Lazy-loaded so its inovua-grid +
// helper chunk is paid only by callers who reach /admin/users/import.
// Inside packages/ops-platform-v3/src/frontend/admin/ per CLAUDE.md
// "do not static-import the new admin page outside [...] frontend/admin/".
const UserImportPage = lazy(() =>
  import('./admin/users/UserImportPage.js').then((m) => ({ default: m.UserImportPage })),
);

// Plan 18 Task 21: dynamic-import the stationDataDashboard module so users
// without technician_* roles don't pay the recharts + html2canvas bundle cost.
const StationDataDashboard = lazy(() =>
  import('./station-data-dashboard/StationDataDashboardRouter.js').then((m) => ({
    default: m.StationDataDashboardRouter,
  })),
);

// materials-02b Phase 0: dynamic-import the BOM module from its own workspace
// package (`@ams/bom/frontend`). Vite emits a separate chunk for the module's
// router and each lazy-loaded route. See
// platformwiki/wiki/patterns/module-package-frontend-mount.md.
const BomRouter = lazy(() => import('@ams/bom/frontend').then((m) => ({ default: m.default })));

// materials-03 Task 27: dynamic-import the Inventory module from its own
// workspace package (`@ams/inventory/frontend`). Vite emits a separate chunk
// for the inventory router shell; Tasks 28-31 add per-route sub-chunks.
const InventoryRouter = lazy(() =>
  import('@ams/inventory/frontend').then((m) => ({ default: m.default })),
);

// email-03 Task 5: dynamic-import the Email module from its own workspace
// package (`@ams/email/frontend`). Default export is the zero-prop
// HostEmailRouter, which builds its HTTP EmailApi from the @ams/shared/auth
// singletons (initAuthSingletons above) and sources callerEmail from /me.
const EmailRouter = lazy(() => import('@ams/email/frontend').then((m) => ({ default: m.default })));

// conference-02 Task 2: dynamic-import the Conference module from its own
// workspace package (`@ams/conference/frontend`). Default export is the
// zero-prop HostConferenceRouter, which builds its HTTP ConferenceApi from
// the @ams/shared/auth singletons and sources callerEmail + privileges
// from /me.
const ConferenceRouter = lazy(() =>
  import('@ams/conference/frontend').then((m) => ({ default: m.default })),
);

// procurement-01 substrate: dynamic-import the Procurement module from its own
// workspace package (`@ams/procurement/frontend`). Default export is the
// zero-prop HostProcurementRouter (a placeholder shell in procurement-01; the
// Quote → PO → Invoice surfaces land in procurement-02+).
const ProcurementRouter = lazy(() =>
  import('@ams/procurement/frontend').then((m) => ({ default: m.default })),
);

// The Commercial Console is a SEPARATE Vite entry (commercial-console.html),
// not a route in this SPA. The AppShell nav links to it with a plain <a> —
// see shell/modules.ts (`external`) and commercial-entry/main.tsx.

function AdminLoading(): JSX.Element {
  return (
    <div className="admin-router">
      <div style={{ padding: 20, color: '#666', fontStyle: 'italic' }}>Loading admin…</div>
    </div>
  );
}

function BomLoading(): JSX.Element {
  return <div style={{ padding: 20, color: '#666', fontStyle: 'italic' }}>Loading BOM…</div>;
}

function InventoryLoading(): JSX.Element {
  return <div style={{ padding: 20, color: '#666', fontStyle: 'italic' }}>Loading Inventory…</div>;
}

function EmailLoading(): JSX.Element {
  return <div style={{ padding: 20, color: '#666', fontStyle: 'italic' }}>Loading Email…</div>;
}

function ConferenceLoading(): JSX.Element {
  return <div style={{ padding: 20, color: '#666', fontStyle: 'italic' }}>Loading Conference…</div>;
}

function ProcurementLoading(): JSX.Element {
  return (
    <div style={{ padding: 20, color: '#666', fontStyle: 'italic' }}>Loading Procurement…</div>
  );
}

export interface AppProps {
  authService?: AuthService;
}

const defaultAuthService = createAuthService({
  apiUrl: import.meta.env.VITE_API_URL,
  csrfManager,
});

export { csrfManager };

/**
 * Plan 16 polish: when a same-origin `/api/*` client throws `CsrfInvalidError`
 * (server rejected the token as missing or invalid), force a full-page redirect
 * to `/login`. Handles unhandled promise rejections at the window scope so the
 * error doesn't need to thread through every call site. Full navigation (not
 * react-router `navigate`) is intentional: it clears any stale component state
 * and re-evaluates `AuthProvider`'s verify effect from a clean slate.
 */
function useCsrfInvalidRedirect(): void {
  useEffect(() => {
    function handle(event: PromiseRejectionEvent): void {
      if (isCsrfInvalidError(event.reason)) {
        event.preventDefault();
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.href = '/login?error=csrf_expired';
        }
      }
    }
    window.addEventListener('unhandledrejection', handle);
    return () => window.removeEventListener('unhandledrejection', handle);
  }, []);
}

export function App({ authService = defaultAuthService }: AppProps = {}): JSX.Element {
  useCsrfInvalidRedirect();
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: 5 * 60 * 1000, retry: 3, refetchOnWindowFocus: false },
        },
      }),
  );
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider service={authService}>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<ResolveHome />} />
              <Route path="expoams/*" element={<ExpoAmsRouter />} />
              {/* Plan 24 — sub-route mounted ABOVE `admin/*` so the more
                  specific path wins. UserImportPage handles its own
                  user.import gate; AdminRouter remains tab-based. */}
              <Route
                path="admin/users/import"
                element={
                  <Suspense fallback={<AdminLoading />}>
                    <UserImportPage />
                  </Suspense>
                }
              />
              <Route
                path="admin/*"
                element={
                  <Suspense fallback={<AdminLoading />}>
                    <AdminRouter />
                  </Suspense>
                }
              />
              <Route
                path="station-data-dashboard/*"
                element={
                  <Suspense fallback={<SddLoading />}>
                    <StationDataDashboard />
                  </Suspense>
                }
              />
              <Route
                path="bom/*"
                element={
                  <Suspense fallback={<BomLoading />}>
                    <BomRouter />
                  </Suspense>
                }
              />
              <Route
                path="inventory/*"
                element={
                  <Suspense fallback={<InventoryLoading />}>
                    <InventoryRouter />
                  </Suspense>
                }
              />
              <Route
                path="email/*"
                element={
                  <Suspense fallback={<EmailLoading />}>
                    <EmailRouter />
                  </Suspense>
                }
              />
              <Route
                path="conference/*"
                element={
                  <Suspense fallback={<ConferenceLoading />}>
                    <ConferenceRouter />
                  </Suspense>
                }
              />
              <Route
                path="procurement/*"
                element={
                  <Suspense fallback={<ProcurementLoading />}>
                    <ProcurementRouter />
                  </Suspense>
                }
              />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
